import React, { ReactElement } from 'react';
import { formatTranslation } from 'intl';
import { CommonTranslationKeys, FieldTranslationKeys, ProjectSetupTranslationKeys } from 'intl/keys';
import { Box, Divider, Grid, IconButton, Menu, MenuItem, Stack, useMediaQuery, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';
import { MemoPreviewField } from 'components/general/PreviewField';
import { SingleContractPresentationalProps } from './types';
import { MemoForbiddenTooltip } from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { Edit, MoreVert } from '@mui/icons-material';
import { ButtonTextIcon, SubheaderText, secondaryActionButtonSx } from 'components/general';
import ViewWrapper from 'components/core/Layout/ViewWrapper';
import { contractSectionPaddingSx } from '../layout';
import { useCallback, useState } from 'react';

export function SingleContractPresentational({
  label,
  parties,
  editDisabledReason,
  isEditButtonDisabled,
  edit,
}: SingleContractPresentationalProps): ReactElement {
  const intl = useIntl();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleEdit = useCallback(() => {
    edit();
    handleMenuClose();
  }, [edit, handleMenuClose]);

  const partiesText = parties && parties.length > 0
    ? parties.map((party) => party.companyShortLabel || (party as any).label || '').join(', ')
    : '-';

  return (
    <ViewWrapper>
      <Box>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          sx={{
            ...contractSectionPaddingSx,
            mt: 4,
          }}
        >
          <SubheaderText sx={{ mt: 0 }}>
            {label}
          </SubheaderText>
          <Stack
            direction='row'
            spacing={1}
            justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
          >
            {isLargeScreen ? (
              <MemoForbiddenTooltip
                visibleWhen={isEditButtonDisabled}
                reason={editDisabledReason}
                inlined
              >
                <ButtonTextIcon
                  disabled={isEditButtonDisabled}
                  startIcon={Edit}
                  onClick={edit}
                  sx={secondaryActionButtonSx}
                >
                  {formatTranslation(intl, CommonTranslationKeys.edit,)}
                </ButtonTextIcon>
              </MemoForbiddenTooltip>
            ) : (
              <>
                <IconButton
                  onClick={handleMenuClick}
                  aria-controls={open ? 'contract-actions-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={open ? 'true' : undefined}
                >
                  <MoreVert />
                </IconButton>
                <Menu
                  id='contract-actions-menu'
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem
                    onClick={handleEdit}
                    disabled={isEditButtonDisabled}
                  >
                    {formatTranslation(intl, CommonTranslationKeys.edit)}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 4 }} />

        <Grid
          container
          spacing={3}
          sx={{
            ...contractSectionPaddingSx,
            columnGap: {
              xs: 0,
              sm: '10%',
            },
          }}
        >
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewField
                label={formatTranslation(intl, FieldTranslationKeys.contractNumbers,)}
                value={label}
                dense={true}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <MemoPreviewField
                label={formatTranslation(intl, ProjectSetupTranslationKeys.parties,)}
                value={partiesText}
                dense={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </ViewWrapper>
  );
}
